import {
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  inquiryApi,
  SupplierInquiry,
  TierPrice,
} from '@texas/api/endpoints/inquiryApi';
import { Icons } from '@texas/components/shared/Icons';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { useTranslation } from 'react-i18next';
import { AddMultipleInquiryTiersModal } from './AddMultipleInquiryTiersModal';
import { EditInquiryTierModal } from './EditInquiryTierModal';
import { inquiryEvents } from './events';

interface Props {
  inquiry: SupplierInquiry;
  isOpen: boolean;
  onClose: () => void;
}

export function TiersEditDrawer({ inquiry, isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const {
    value: tierToDelete,
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useValueDisclosure<number>();
  const {
    value: tierToEdit,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<TierPrice>();
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  return (
    <>
      <Drawer placement="right" size="md" isOpen={isOpen} onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>{t('inquiry.editTiers')}</DrawerHeader>
          <DrawerBody>
            <Flex direction="column" gap={1} pt={2}>
              {inquiry.tierPrices
                .slice()
                .sort((a, b) => a.quantity - b.quantity)
                .map((tier) => {
                  const isTargetTier = inquiry.allTargetTiers.some(
                    (targetTier) => targetTier.quantity === tier.quantity,
                  );
                  return (
                    <Flex
                      key={tier.id}
                      align="center"
                      justify="space-between"
                      px={3}
                      py={2}
                      borderRadius="md"
                      bg="texas.bg.800"
                      _light={{ bg: 'gray.50' }}
                      cursor="pointer"
                      onClick={() => onEditOpen(tier)}
                      _hover={{ opacity: 0.8 }}
                    >
                      <Flex align="center" gap={2}>
                        <Text
                          fontFamily="barlow"
                          fontWeight="bold"
                          fontSize="sm"
                        >
                          {t('general.quantityCount', {
                            count: tier.quantity,
                          })}
                        </Text>
                        {isTargetTier && (
                          <Badge colorScheme="blue" size="sm">
                            {t('inquiry.targetTier')}
                          </Badge>
                        )}
                      </Flex>
                      <Flex gap={1} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          icon={<Icons.Pencil boxSize={3} />}
                          aria-label={t('general.edit')}
                          size="xs"
                          variant="no-bg"
                          onClick={() => onEditOpen(tier)}
                        />
                        <Tooltip label={t('general.clickToRemove')}>
                          <IconButton
                            icon={<Icons.Close boxSize={3} />}
                            aria-label={t('general.clickToRemove')}
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => onDeleteOpen(tier.id)}
                          />
                        </Tooltip>
                      </Flex>
                    </Flex>
                  );
                })}
            </Flex>
            <Button
              mt={3}
              color="black"
              leftIcon={<Icons.Plus />}
              variant="texas-outline"
              onClick={onAddOpen}
            >
              {t('tiers.addMultiple')}
            </Button>
          </DrawerBody>
          <DrawerFooter gap={2}>
            <Flex flex={1} justify="flex-end">
              <Button size="sm" variant="texas-light" onClick={onClose}>
                {t('general.close')}
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      {tierToEdit && (
        <EditInquiryTierModal
          inquiry={inquiry}
          tier={tierToEdit}
          showPrice={false}
          isOpen={isEditOpen}
          onClose={onEditClose}
        />
      )}
      <AddMultipleInquiryTiersModal
        inquiry={inquiry}
        isOpen={isAddOpen}
        onClose={onAddClose}
      />
      <VerifyDialogWithRequest
        headerTitle={t('inquiry.confirmDeleteTierHeader')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.remove')}
        primaryRequest={inquiryApi.deleteTierPrice}
        args={[inquiry.id, tierToDelete ?? 0]}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onPerformed={() => {
          inquiryEvents.onUpdated.dispatch();
          onDeleteClose();
        }}
        onSuccessTitle={t('general.saved')}
      >
        {t('inquiry.confirmDeleteTier')}
      </VerifyDialogWithRequest>
    </>
  );
}
